import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Download, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { formatDay, formatMinor, todayIso } from "../../lib/finance";
import { AGE_LABELS, billingError, canWrite } from "../../lib/billing";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Skeleton } from "../ui/skeleton";

const perCurrency = (totals) => Object.entries(totals || {}).sort(([a], [b]) => a.localeCompare(b)).map(([c, v]) => formatMinor(v, c));
const EXPORTS = [["subscriptions", "Abonnements"], ["installments", "Échéances"], ["payments", "Encaissements"]];
const MOVEMENTS = [["new", "Nouveaux abonnements"], ["upgrade", "Changements d'offre à la hausse"], ["downgrade", "Changements d'offre à la baisse"],
  ["churn", "Fins d'abonnement (hors changement d'offre)"]];

function Figure({ label, values, hint, testid }) {
  return (
    <div className="space-y-0.5" data-testid={testid}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-lg font-semibold text-slate-900 tabular-nums">{values.length ? values.map((v) => <span key={v} className="block">{v}</span>) : "—"}</div>
      {hint && <div className="text-xs text-slate-500">{hint}</div>}
    </div>
  );
}

// « Indicateurs » of Factures et échéances (Ordre 17-4, D-50): per currency, never converted; definitions written under each figure.
export default function BillingIndicators() {
  const { user } = useAuth();
  const monthStart = `${todayIso().slice(0, 8)}01`;
  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(todayIso());
  const [data, setData] = useState(null);
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState("");

  const load = useCallback(async () => {
    setPhase("loading");
    try {
      const { data: body } = await api.get("/billing/indicators", { params: { from, to } });
      setData(body);
      setPhase("ready");
    } catch (e) {
      setError(billingError(e, "Impossible de calculer les indicateurs."));
      setPhase("error");
    }
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const download = async (kind) => {
    setExporting(kind);
    try {
      const { data: blob } = await api.get(`/billing/export/${kind}.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `revenus-${kind}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export téléchargé");
    } catch (e) {
      toast.error("Export impossible. Réessayez dans quelques instants.");
    } finally {
      setExporting("");
    }
  };

  return (
    <div className="space-y-4" data-testid="billing-indicators">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label htmlFor="billing-ind-from">Du</Label>
          <Input id="billing-ind-from" type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="h-9" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="billing-ind-to">Au</Label>
          <Input id="billing-ind-to" type="date" value={to} min={from} max={todayIso()} onChange={(e) => setTo(e.target.value)} className="h-9" />
        </div>
        {canWrite(user, "billing:export") && (
          <div className="flex flex-wrap gap-2 ml-auto">
            {EXPORTS.map(([kind, label]) => (
              <Button key={kind} variant="outline" size="sm" onClick={() => download(kind)} disabled={!!exporting} data-testid={`billing-export-${kind}`}>
                {exporting === kind ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Download size={14} className="mr-1.5" />}{label}
              </Button>
            ))}
          </div>
        )}
      </div>

      {phase === "loading" && <Skeleton className="h-40 w-full" />}
      {phase === "error" && (
        <Card className="p-6 text-center space-y-3">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700">{error}</p>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}
      {phase === "ready" && data && (
        <>
          <Card className="p-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Figure label="Revenu mensuel récurrent (MRR)" values={perCurrency(data.mrr)} testid="billing-ind-mrr"
              hint={`Abonnements en cours le ${formatDay(data.as_of)}, ramenés au mois`} />
            <Figure label="Revenu annuel (ARR)" values={perCurrency(data.arr)} hint="MRR × 12" testid="billing-ind-arr" />
            <Figure label="Facturé" values={perCurrency(data.billed)} hint="Factures de la période, moins les avoirs" testid="billing-ind-billed" />
            <Figure label="Encaissé" values={perCurrency(data.collected)} hint="Encaissements de la période, moins les remboursements" testid="billing-ind-collected" />
          </Card>
          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="p-4 space-y-2" data-testid="billing-ind-overdue">
              <h3 className="font-semibold text-slate-900">Impayés au {formatDay(data.as_of)}</h3>
              {Object.keys(data.overdue).length === 0 ? <p className="text-sm text-slate-500">Aucune échéance en retard.</p> : (
                <table className="w-full text-sm tabular-nums">
                  <thead><tr className="text-xs text-slate-500"><th className="text-left font-medium py-1">Retard</th>
                    {Object.keys(data.overdue).map((c) => <th key={c} className="text-right font-medium py-1">{c}</th>)}</tr></thead>
                  <tbody>
                    {Object.entries(AGE_LABELS).map(([age, label]) => (
                      <tr key={age} className="border-t border-slate-100">
                        <td className="py-1.5">{label}</td>
                        {Object.entries(data.overdue).map(([c, buckets]) => <td key={c} className="text-right">{formatMinor(buckets[age] || 0, c)}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
            <Card className="p-4 space-y-2" data-testid="billing-ind-movements">
              <h3 className="font-semibold text-slate-900">Mouvements du {formatDay(data.period.from)} au {formatDay(data.period.to)}</h3>
              <ul className="text-sm divide-y divide-slate-100">
                {MOVEMENTS.map(([key, label]) => (
                  <li key={key} className="py-1.5 flex flex-wrap justify-between gap-2">
                    <span>{label}</span>
                    <span className="tabular-nums text-slate-700">{data.movements[key].count}{perCurrency(data.movements[key].mrr).length ? ` · ${perCurrency(data.movements[key].mrr).join(" · ")} / mois` : ""}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-slate-500">{data.active_subscriptions} abonnement{data.active_subscriptions > 1 ? "s" : ""} en cours · {data.trials} en essai{data.trials_ended ? ` (dont ${data.trials_ended} terminé${data.trials_ended > 1 ? "s" : ""})` : ""}</p>
            </Card>
          </div>
          <p className="text-xs text-slate-500">Chaque montant reste dans sa devise : rien n'est additionné entre devises ni converti.</p>
        </>
      )}
    </div>
  );
}
